import React, { useState } from 'react';
import {
  Box, Typography, Button, Paper, Divider, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import QuizList      from './QuizList';
import QuizIntro     from './QuizIntro';
import QuizQuestions from './QuizQuestions';
import QuizResult    from './QuizResult';
import StudentQuizAPIs from 'utilities/api/StudentQuizAPIs';

const C = {
  navy:      '#273F77',
  orange:    '#E79A2B',
  bg:        '#f5f6fa',
  cardBg:    '#ffffff',
  border:    '#e8eaf0',
  textMuted: '#8492a6',
};

const STEP = {
  LIST:      'list',
  INTRO:     'intro',
  QUESTIONS: 'questions',
  RESULT:    'result',
};

// ─── Breadcrumb ───────────────────────────────────────────────────────────────
const Breadcrumb = ({ step, quizTitle, moduleName }) => {
  const crumbs = {
    [STEP.LIST]:      ['Training', 'Quizzes'],
    [STEP.INTRO]:     ['Training', 'Quizzes', quizTitle],
    [STEP.QUESTIONS]: ['Training', 'Quizzes', quizTitle, 'Questions'],
    [STEP.RESULT]:    ['Training', 'Quizzes', quizTitle, 'Result'],
  }[step] || [];

  return (
    <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
      {crumbs.map((c, i) => (
        <React.Fragment key={c + i}>
          {i > 0 && (
            <Typography variant="caption" color={C.textMuted}>/</Typography>
          )}
          <Typography
            variant="caption"
            fontWeight={i === crumbs.length - 1 ? 700 : 400}
            color={i === crumbs.length - 1 ? C.navy : C.textMuted}
          >
            {c}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );
};

// ─── Status pill ──────────────────────────────────────────────────────────────
const StatusPill = ({ label, color, bg }) => (
  <Box sx={{
    display: 'inline-flex', alignItems: 'center', gap: 0.5,
    px: 1.25, py: 0.25, borderRadius: '20px', backgroundColor: bg,
  }}>
    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color }} />
    <Typography variant="caption" fontWeight={700} color={color} lineHeight={1.6}>
      {label}
    </Typography>
  </Box>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
const QuizPage = ({ quizzes, moduleName, onBack }) => {
  const [step, setStep]               = useState(quizzes.length === 1 ? STEP.INTRO : STEP.LIST);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [questions, setQuestions]     = useState([]);
  const [attemptsInfo, setAttemptsInfo] = useState(null);
  const [result, setResult]           = useState(null);
  const [loading, setLoading]         = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  // map of quiz_id → { passed, score, attempt_status, attempts_made, attempts_remaining }
  const [quizStatuses, setQuizStatuses] = useState({});

  // Fetch attempt status for ALL quizzes upfront so QuizList can show pass/fail badges
  React.useEffect(() => {
    const fetchAll = async () => {
      const entries = await Promise.all(
        quizzes.map(async (q) => {
          try {
            const data = await StudentQuizAPIs.AttemptsRemaining(q.id);
            return [q.id, data];
          } catch {
            return [q.id, null];
          }
        })
      );
      setQuizStatuses(Object.fromEntries(entries));
    };
    fetchAll();

    // Auto-select if only 1 quiz
    if (quizzes.length === 1) {
      handleSelectQuiz(quizzes[0]);
    }
  }, []); // eslint-disable-line

  const handleSelectQuiz = async (quiz) => {
    setSelectedQuiz(quiz);
    setStep(STEP.INTRO);
    setLoading(true);
    try {
      const data = await StudentQuizAPIs.AttemptsRemaining(quiz.id);
      setAttemptsInfo(data);
    } catch (err) {
      console.error('Failed to fetch attempts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    setLoading(true);
    try {
      await StudentQuizAPIs.UpdateStatus(selectedQuiz.id, 'in progress');
      const data = await StudentQuizAPIs.QuestionsGet(selectedQuiz.id);
      setQuestions(data);
      setStep(STEP.QUESTIONS);
    } catch (err) {
      console.error('Failed to start quiz:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (answers) => {
    setSubmitting(true);
    try {
      const data = await StudentQuizAPIs.CalculateScore(selectedQuiz.id, answers);
      setResult(data);
      setStep(STEP.RESULT);
      // Refresh this quiz's status in the list
      try {
        const updated = await StudentQuizAPIs.AttemptsRemaining(selectedQuiz.id);
        setQuizStatuses((prev) => ({ ...prev, [selectedQuiz.id]: updated }));
      } catch {}
    } catch (err) {
      console.error('Failed to submit quiz:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = async () => {
    setResult(null);
    setQuestions([]);
    await handleSelectQuiz(selectedQuiz);
  };

  const handleBackButton = () => {
    if (step === STEP.INTRO && quizzes.length > 1) {
      setStep(STEP.LIST);
      setSelectedQuiz(null);
      setAttemptsInfo(null);
    } else {
      onBack();
    }
  };

  const canGoBack = step !== STEP.QUESTIONS;

  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: 1200,
        backgroundColor: C.bg,
        overflowY: 'auto',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* ── Top bar ── */}
      <Paper
        elevation={0}
        square
        sx={{
          position: 'sticky', top: 0, zIndex: 10,
          borderBottom: `1px solid ${C.border}`,
          px: { xs: 2, sm: 4 },
          height: 64,
          display: 'flex', alignItems: 'center', gap: 2,
          backgroundColor: C.cardBg,
          flexShrink: 0,
        }}
      >
        {/* Back button */}
        {canGoBack && (
          <>
            <Button
              startIcon={<ArrowBackIcon sx={{ fontSize: '18px !important' }} />}
              onClick={handleBackButton}
              size="small"
              sx={{
                color: C.textMuted, fontWeight: 600, textTransform: 'none',
                flexShrink: 0, borderRadius: '8px',
                '&:hover': { backgroundColor: C.bg, color: C.navy },
              }}
            >
              {step === STEP.RESULT ? 'Modules'
                : step === STEP.INTRO && quizzes.length > 1 ? 'All Quizzes'
                : 'Modules'}
            </Button>
            <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />
          </>
        )}

        {/* Breadcrumb */}
        <Box flex={1} minWidth={0}>
          <Breadcrumb
            step={step}
            quizTitle={selectedQuiz?.title}
            moduleName={moduleName}
          />
          {step === STEP.QUESTIONS && (
            <Typography variant="subtitle2" fontWeight={700} color={C.navy} noWrap>
              {selectedQuiz?.title}
            </Typography>
          )}
        </Box>

        {/* Right pills */}
        <Box display="flex" alignItems="center" gap={1.5} flexShrink={0}>
          {step === STEP.QUESTIONS && (
            <StatusPill
              label={`${questions.length} questions`}
              color={C.navy}
              bg="rgba(39,63,119,0.08)"
            />
          )}
          {step === STEP.INTRO && attemptsInfo && (
            <StatusPill
              label={`${attemptsInfo.attempts_remaining} attempt${attemptsInfo.attempts_remaining !== 1 ? 's' : ''} left`}
              color={attemptsInfo.attempts_remaining === 0 ? '#c62828' : '#2e7d32'}
              bg={attemptsInfo.attempts_remaining === 0 ? 'rgba(198,40,40,0.1)' : 'rgba(46,125,50,0.1)'}
            />
          )}
          <StatusPill
            label={moduleName}
            color={C.navy}
            bg="rgba(39,63,119,0.08)"
          />
        </Box>
      </Paper>

      {/* ── Page loading ── */}
      {loading && step !== STEP.INTRO && (
        <Box display="flex" justifyContent="center" alignItems="center" py={12}>
          <CircularProgress sx={{ color: C.orange }} />
        </Box>
      )}

      {/* ── Content ── */}
      {(!loading || step === STEP.INTRO) && (
        <Box px={{ xs: 2, sm: 4, md: 6 }} py={4} flex={1}>
          {step === STEP.LIST && (
            <QuizList quizzes={quizzes} moduleName={moduleName} onSelect={handleSelectQuiz} quizStatuses={quizStatuses} />
          )}
          {step === STEP.INTRO && selectedQuiz && (
            <QuizIntro
              quiz={selectedQuiz}
              attemptsInfo={attemptsInfo}
              onStart={handleStart}
              loading={loading}
            />
          )}
          {step === STEP.QUESTIONS && questions.length > 0 && (
            <QuizQuestions
              questions={questions}
              onSubmit={handleSubmit}
              submitting={submitting}
            />
          )}
          {step === STEP.RESULT && result && (
            <QuizResult
              result={result}
              onClose={onBack}
              onRetry={handleRetry}
              canRetry={result.remaining_attempts > 0}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default QuizPage;